import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import styles from './CargoModal.module.css'
import classNames from 'classnames'
import {
  setIsVisibleModalCargo,
  fetchAddCargo,
  setCurrentCargo,
  fetchCargos,
  setIsDisabledEdit,
  setCurrentCargoGroupIdClient,
  setNewCargoReportIdPrefill,
  fetchDeleteCargoImage,
  setIsOnlyCargo,
} from '../../../redux/reducers/cargos'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../../redux/store'
import { clientsSelector } from '../../../redux/selectors/clients-selector'
import { fetchClients } from '../../../redux/reducers/clients'
import { onlyNumberRegularCheck } from '../../../utils/onlyNumberRegularCheck'
import { reportsSelector } from '../../../redux/selectors/reports-selector'
import { formatDate } from '../../../utils/date'
import {
  cargoReadinessAtToPickerValue,
  parseCargoIsPaid,
  parseCargoReadyForPickup,
} from '../report-modal/cargos/cargoReadinessDate'
import DateTimePicker from '../../common/date-picker/DatePicker'
import reportModalStyles from '../report-modal/ReportModal.module.css'
import { cargosSelector } from '../../../redux/selectors/cargos-selector'
import { invoicesSelector } from '../../../redux/selectors/invoices-selector'
import {
  fetchCurrentInvoices,
  fetchInvoices,
} from '../../../redux/reducers/invoices'
import {
  fetchAllReportGroupedCargos,
  fetchCurrentReport,
  fetchReportGroupedCargos,
} from '../../../redux/reducers/reports'
import { default as Select } from 'react-select'
import { getFiltersFromSessionStorage } from '../../../utils/filterUtils'
import { useClearBodyNoScrollClass } from '../../../hooks/use-clear-body-no-scroll-slass'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import { toast } from 'react-toastify'
import { getRole } from '../../../utils/getRole'
import { processFilesWithHeicConversion } from '../../../utils/heicUtils'

interface ClientOption {
  value: number
  label: string
}

/** Не пересекается с id клиентов в БД; в state кладём '' → to_id не уходит в FormData */
const RECIPIENT_NOT_SELECTED_VALUE = -1
const RECIPIENT_NOT_SELECTED_OPTION: ClientOption = {
  value: RECIPIENT_NOT_SELECTED_VALUE,
  label: '-Не выбран-',
}

type FileArray = File[]

/** Пустое поле в форме: без «0», API null/undefined/0 → пустая строка */
function cargoNumericFieldFromApi(value: unknown): string {
  if (value == null || value === '') return ''
  const n =
    typeof value === 'number'
      ? value
      : Number(String(value).replace(/\s/g, '').replace(',', '.'))
  if (!Number.isFinite(n) || n === 0) return ''
  return String(n)
}

/** В корне API может отдать to_id/from_id = 0; тогда берём id из вложенного клиента */
function clientIdSelectStringFromCargo(
  flatId: number | null | undefined,
  nested?: { id?: number | null } | null
): string {
  if (flatId != null && flatId > 0) return String(flatId)
  const nid = nested?.id
  if (nid != null && nid > 0) return String(nid)
  return ''
}

function CargoModal() {
  const dispatch = useDispatch<AppDispatch>()
  const { clients } = useSelector(clientsSelector)
  const { reports, isPendingGetAllReports, currentReport, currentReportId } =
    useSelector(reportsSelector)
  const {
    currentCargo,
    isDisabledEdit,
    currentCargoGroupIdClient,
    isPendingAddCargos,
    isOnlyCargo,
    newCargoReportIdPrefill,
    cargoModalReportTabConstraints,
  } = useSelector(cargosSelector)

  const lockPlFromReportTab = Boolean(cargoModalReportTabConstraints?.lockPl)
  useClearBodyNoScrollClass()
  const { currentInvoice } = useSelector(invoicesSelector)

  const { isClient, isDriver, isAdmin, isOwner, userId } = getRole()
  const canEditIsPaid = isAdmin || isOwner
  const isCargoModalFromReportTab = Boolean(
    isOnlyCargo || cargoModalReportTabConstraints?.lockPl
  )
  const isReportCargoLoadingTab =
    (cargoModalReportTabConstraints?.listKind ?? 'loading') === 'loading'

  useEffect(() => {
    if (isDriver) {
      void dispatch(fetchClients())
    }
  }, [dispatch, isDriver])

  const [formData, setFormData] = useState({
    currency: '',
    clientIdFrom: '',
    clientIdTo: '',
    payer: 0,
    reportId: '',
    description: '',
    volume: '',
    weight: '',
    amount: '',
    paymentMethod: 'bank_transfer' as 'cash' | 'bank_transfer',
    isPaid: false,
    hasTn: false,
    tnNumber: '',
    thirdId: '', // Добавляем поле для третьего лица
    readinessAt: '',
    canBePickedUp: false,
  })

  const [errors, setErrors] = useState({
    reportId: false,
    clientIdFrom: false,
    clientIdTo: false,
  })

  // Состояние для фотографий
  const [selectedPhoto, setSelectedPhoto] = useState<string[]>([])
  const [selectedPhotoForSend, setSelectedPhotoForSend] = useState<File[]>([])
  const [photos, setPhotos] = useState<any[]>([])
  const [isVisibleDeleteIconModal, setIsVisibleDeleteIconModal] =
    useState(false)
  const [imageToDelete, setImageToDelete] = useState<number | null>(null)

  const resetForm = () => {
    setFormData({
      currency: 'BYN',
      clientIdFrom: '',
      clientIdTo: '',
      payer: 0,
      reportId: '',
      description: '',
      volume: '',
      weight: '',
      amount: '',
      paymentMethod: 'bank_transfer' as 'cash' | 'bank_transfer',
      isPaid: false,
      hasTn: false,
      tnNumber: '',
      thirdId: '', // Добавляем поле для третьего лица
      readinessAt: '',
      canBePickedUp: false,
    })
    setErrors({
      reportId: false,
      clientIdFrom: false,
      clientIdTo: false,
    })
    // Сбрасываем фото
    setSelectedPhoto([])
    setSelectedPhotoForSend([])
    setPhotos([])
  }

  useEffect(() => {
    if (currentCargo) {
      setFormData({
        currency: currentCargo.currency || 'BYN',
        clientIdFrom: clientIdSelectStringFromCargo(
          currentCargo.from_id,
          currentCargo.from
        ),
        clientIdTo: clientIdSelectStringFromCargo(
          currentCargo.to_id,
          currentCargo.to
        ),
        payer: currentCargo.payer || 0,
        reportId: currentCargo.report_id?.toString() || '',
        description: currentCargo.description || '',
        volume: cargoNumericFieldFromApi(currentCargo.volume),
        weight: cargoNumericFieldFromApi(currentCargo.weight),
        amount: cargoNumericFieldFromApi(currentCargo.amount),
        paymentMethod: currentCargo.payment_method || 'bank_transfer',
        isPaid: parseCargoIsPaid(currentCargo.is_paid),
        hasTn: currentCargo.has_tn || false,
        tnNumber: currentCargo.tn_number?.toString() || '',
        thirdId: currentCargo.third_id?.toString() || '', // Добавляем поле для третьего лица
        readinessAt: cargoReadinessAtToPickerValue(currentCargo.readiness_at),
        canBePickedUp: parseCargoReadyForPickup(
          currentCargo.can_be_picked_up
        ),
      })

      if (currentCargo.images && currentCargo.images.length > 0) {
        setPhotos(currentCargo.images)
      } else {
        setPhotos([])
      }
    } else {
      setPhotos([])
    }

    if (!currentCargo && currentCargoGroupIdClient) {
      const clientId = String(currentCargoGroupIdClient)
      const listKind = cargoModalReportTabConstraints?.listKind ?? 'loading'
      setFormData((prev) =>
        listKind === 'unloading'
          ? { ...prev, clientIdTo: clientId }
          : { ...prev, clientIdFrom: clientId }
      )
    }
  }, [
    currentCargo,
    currentCargoGroupIdClient,
    cargoModalReportTabConstraints?.listKind,
  ])

  /** Новый груз из вкладки путевого (явный id из Redux, без опоры на «текущий путевой» в другом слайсе) */
  useEffect(() => {
    if (currentCargo) return
    if (!isOnlyCargo) return
    if (newCargoReportIdPrefill == null) return
    const id = newCargoReportIdPrefill
    setFormData((prev) => ({
      ...prev,
      reportId: String(id),
    }))
    dispatch(setNewCargoReportIdPrefill(null))
  }, [currentCargo, dispatch, isOnlyCargo, newCargoReportIdPrefill])

  // Проверка, что файл — HEIC (браузер не показывает HEIC в <img>, сервер может не иметь Imagick)
  const handlePhotoChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    if (!event.target.files) return
    const files = Array.from(event.target.files)
    const maxSize = 50 * 1024 * 1024 // 50MB

    for (let i = 0; i < files.length; i++) {
      if (files[i].size > maxSize) {
        toast.error(`Файл ${files[i].name} превышает максимальный размер 50MB.`)
        return
      }
    }

    const { files: filesToAdd, urls: urlsToAdd } =
      await processFilesWithHeicConversion(files, (fileName, message) => {
        toast.error(fileName ? `${fileName}: ${message}` : message)
      })

    if (filesToAdd.length > 0) {
      setSelectedPhotoForSend((prev: FileArray) => [...prev, ...filesToAdd])
      setSelectedPhoto((prev: string[]) => [...prev, ...urlsToAdd])
    }
  }

  const handleDeletePhoto = (photo: string) => {
    setSelectedPhoto((currentPhotos) =>
      currentPhotos.filter((e) => e !== photo)
    )

    const index = selectedPhoto.findIndex((e) => e === photo)

    setSelectedPhotoForSend((currentFiles) =>
      currentFiles.filter((_, i) => i !== index)
    )
  }

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'payer' ? Number(value) : value,
    }))

    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: false }))
    }
  }

  const handleSelectChange = (
    selectedOption: ClientOption | null,
    fieldName: string
  ) => {
    let nextValue = ''
    if (selectedOption) {
      if (
        fieldName === 'clientIdTo' &&
        selectedOption.value === RECIPIENT_NOT_SELECTED_VALUE
      ) {
        nextValue = ''
      } else {
        nextValue = String(selectedOption.value)
      }
    }
    const fakeEvent = {
      target: {
        name: fieldName,
        value: nextValue,
      },
    } as React.ChangeEvent<HTMLSelectElement>

    handleChange(fakeEvent)
  }

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (onlyNumberRegularCheck(e)) {
      setFormData((prev) => ({
        ...prev,
        [name]: value.replace(',', '.'),
      }))

      if (errors[name as keyof typeof errors]) {
        setErrors((prev) => ({ ...prev, [name]: false }))
      }
    }
  }

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handlePayerChange = (payerValue: number) => {
    setFormData((prev) => ({
      ...prev,
      payer: payerValue,
      // Очищаем thirdId если выбран не третий плательщик
      thirdId: payerValue === 2 ? prev.thirdId : '',
    }))
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target
    if (name === 'canBePickedUp') {
      setFormData((prev) => ({ ...prev, canBePickedUp: checked }))
      return
    }
    if (name === 'isPaid') {
      if (!canEditIsPaid) return
      setFormData((prev) => ({ ...prev, isPaid: checked }))
      return
    }
    setFormData((prev) => ({
      ...prev,
      hasTn: checked,
      tnNumber: checked ? prev.tnNumber : '',
    }))
  }

  const handleTnNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setFormData((prev) => ({
      ...prev,
      tnNumber: value,
    }))
  }

  const checkValidation = () => {
    const recipientRequired = !isCargoModalFromReportTab
    const newErrors = {
      reportId: !formData.reportId,
      clientIdFrom: !formData.clientIdFrom,
      clientIdTo: recipientRequired ? !formData.clientIdTo : false,
    }

    setErrors(newErrors)
    return Object.values(newErrors).some((error) => error)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    handleSave(false)
  }

  useEffect(() => {
    const from = clients.find(
      (item) => String(item.id) === formData.clientIdFrom
    )
    const to = clients.find((item) => String(item.id) === formData.clientIdTo)
    const third = clients.find((item) => String(item.id) === formData.thirdId)

    if (formData.payer === 0) {
      if (from) {
        setFormData((prev) => ({
          ...prev,
          currency: from.currency,
        }))
      }
    } else if (formData.payer === 1) {
      if (to) {
        setFormData((prev) => ({
          ...prev,
          currency: to.currency,
        }))
      }
    } else if (formData.payer === 2) {
      if (third) {
        setFormData((prev) => ({
          ...prev,
          currency: third.currency,
        }))
      }
    }
  }, [
    clients,
    formData.clientIdFrom,
    formData.clientIdTo,
    formData.payer,
    formData.thirdId,
  ])

  useEffect(() => {
    return () => {
      dispatch(setCurrentCargoGroupIdClient(null))
    }
  }, [dispatch])

  // Отдельный useEffect для синхронизации photos с currentCargo.images
  useEffect(() => {
    if (currentCargo?.images) {
      setPhotos(currentCargo.images)
    } else {
      setPhotos([])
    }
  }, [currentCargo?.images, currentCargo])

  const toClientOption = (
    client?: {
      id: number
      entity_name?: string
      name?: string
    } | null
  ): ClientOption | null => {
    if (!client?.id) return null
    return {
      value: client.id,
      label: client.entity_name ? client.entity_name : client.name || '',
    }
  }

  const clientOptions = useMemo<ClientOption[]>(
    () =>
      [...clients]
        .sort((a, b) => {
          const nameA = (a.entity_name || a.name).toLowerCase()
          const nameB = (b.entity_name || b.name).toLowerCase()
          return nameA.localeCompare(nameB)
        })
        .map((item) => ({
          value: item.id,
          label: item.entity_name ? item.entity_name : item.name,
        })),
    [clients]
  )

  const withFallbackClientOption = (
    options: ClientOption[],
    fallback: ClientOption | null
  ): ClientOption[] => {
    if (!fallback) return options
    const exists = options.some((opt) => opt.value === fallback.value)
    return exists ? options : [fallback, ...options]
  }

  const recipientSelectOptions = useMemo(() => {
    const base =
      isClient && currentCargo?.to
        ? withFallbackClientOption([], toClientOption(currentCargo.to))
        : withFallbackClientOption(
            clientOptions,
            toClientOption(currentCargo?.to)
          )
    return [RECIPIENT_NOT_SELECTED_OPTION, ...base]
  }, [isClient, currentCargo, clientOptions])

  const handleSave = (openNewForm: boolean = false) => {
    const hasErrors = checkValidation()
    if (hasErrors) return

    // Подготавливаем данные для отправки
    const cargoData = {
      ...formData,
      photos: selectedPhotoForSend,
      images_remove: currentCargo?.images
        ? currentCargo.images
            .filter(
              (existingPhoto) =>
                !photos.some((photo) => photo.id === existingPhoto.id)
            )
            .map((photo) => photo.id)
        : [],
      ...(!currentCargo && cargoModalReportTabConstraints?.direction
        ? { direction: cargoModalReportTabConstraints.direction }
        : {}),
    }

    dispatch(fetchAddCargo(cargoData)).then((res) => {
      if (!fetchAddCargo.fulfilled.match(res)) return

      if (isOnlyCargo) {
        const reportId = Number(
          formData.reportId ||
            currentCargo?.report_id ||
            currentReport?.id ||
            currentReportId
        )
        if (Number.isFinite(reportId) && reportId > 0) {
          const tabDir = cargoModalReportTabConstraints?.direction
          if (tabDir === 'forward' || tabDir === 'backward') {
            void dispatch(
              fetchReportGroupedCargos({
                reportId,
                direction: tabDir,
              })
            )
          } else {
            void dispatch(fetchAllReportGroupedCargos(reportId))
          }
          void dispatch(fetchCurrentReport(reportId))
        }
      }

      dispatch(setCurrentCargo(null))

      if (!openNewForm) {
        dispatch(setIsVisibleModalCargo(false))
        dispatch(setIsOnlyCargo(false))
        const filters = getFiltersFromSessionStorage()

        if (!isOnlyCargo && filters) {
          dispatch(fetchInvoices(filters))
        }
        resetForm()
        dispatch(setIsDisabledEdit(false))

        if (currentInvoice?.id && !isOnlyCargo) {
          dispatch(fetchCurrentInvoices(currentInvoice?.id))
        }
      } else {
        const created = res.payload
        const clientIdFromNext =
          created?.from_id != null && created.from_id > 0
            ? String(created.from_id)
            : formData.clientIdFrom

        const preservedData = {
          reportId: formData.reportId,
          clientIdFrom: clientIdFromNext,
          clientIdTo: '',
          payer: formData.payer,
          currency: formData.currency,
          thirdId: formData.thirdId,
        }

        resetForm()

        setFormData((prev) => ({
          ...prev,
          ...preservedData,
        }))

        setErrors({
          reportId: false,
          clientIdFrom: false,
          clientIdTo: false,
        })
      }
    })
  }

  const handleSaveAndAddAnother = (e: React.MouseEvent) => {
    e.preventDefault()
    handleSave(true)
  }

  const textareaRefFull = useRef(null)
  const formRef = useRef<HTMLFormElement>(null)

  useLayoutEffect(() => {
    if (textareaRefFull.current) {
      //@ts-ignore
      textareaRefFull.current.style.height = 'inherit'
      //@ts-ignore
      textareaRefFull.current.style.height = `${textareaRefFull.current.scrollHeight}px`
    }
  }, [formData.description])

  useEffect(() => {
    return () => {
      dispatch(setIsOnlyCargo(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div style={{ display: 'block' }} className={styles.win}>
      <div className={styles.winContainer}>
        <div className={styles.winContent}>
          <div
            onClick={() => {
              dispatch(setCurrentCargo(null))
              dispatch(setIsOnlyCargo(false))
              dispatch(setIsVisibleModalCargo(false))
              if (currentInvoice?.id) {
                dispatch(fetchCurrentInvoices(currentInvoice?.id))
              }
              // dispatch(setCargosGroup(null))
            }}
            className={styles.close}
          >
            ×
          </div>
          <div className={styles.winContentFulltext}>
            <h3
              style={{ textAlign: 'center' }}
              className={classNames(`mb-5 ${styles.h3}`)}
            >
              Груз
            </h3>
            <form ref={formRef} onSubmit={handleSubmit}>
              <div className="row align-items-start">
                <div className="col-12 mb-3">
                  <small className={classNames('d-block mb-1', styles.small2)}>
                    Путевой лист*
                  </small>
                  <select
                    name="reportId"
                    value={formData.reportId}
                    onChange={handleChange}
                    className={errors.reportId ? styles.isError : ''}
                    disabled={isDisabledEdit || isClient || lockPlFromReportTab}
                  >
                    <option value="">Выберите путевой лист</option>
                    {isPendingGetAllReports ? (
                      <option value="" disabled>
                        Загрузка путевых...
                      </option>
                    ) : isClient && currentCargo?.report ? (
                      <option value={currentCargo.report.id}>
                        {currentCargo.report.number
                          ? `№${currentCargo.report.number}`
                          : 'б/н'}{' '}
                        от {formatDate(currentCargo.report.departure)}{' '}
                        {currentCargo.report.car?.name}
                      </option>
                    ) : (
                      reports.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.number ? `№${item.number}` : 'б/н'} от{' '}
                          {formatDate(item.departure)} {item.car?.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="col-12 col-sm-6 mb-3">
                  <small className={classNames('d-block mb-1', styles.small2)}>
                    Отправитель*
                  </small>
                  <Select<ClientOption>
                    options={
                      isClient && currentCargo?.from
                        ? withFallbackClientOption(
                            [],
                            toClientOption(currentCargo.from)
                          )
                        : withFallbackClientOption(
                            clientOptions,
                            toClientOption(currentCargo?.from)
                          )
                    }
                    value={
                      formData.clientIdFrom
                        ? isClient && currentCargo?.from
                          ? toClientOption(currentCargo.from)
                          : clientOptions.find(
                              (item) =>
                                String(item.value) === formData.clientIdFrom
                            ) || toClientOption(currentCargo?.from)
                        : null
                    }
                    onChange={(selectedOption) =>
                      handleSelectChange(selectedOption, 'clientIdFrom')
                    }
                    isDisabled={isDisabledEdit || isClient}
                    placeholder="Выберите клиента"
                    noOptionsMessage={() => 'Клиент не найден'}
                    styles={{
                      control: (base, { isFocused }) => ({
                        ...base,
                        minHeight: '38px',
                        height: '38px',
                        borderColor: errors.clientIdFrom
                          ? 'red'
                          : isFocused
                            ? '#0a4475'
                            : '#ccc',
                        boxShadow: isFocused
                          ? 'inset 1px 3px 5px 0px #0000001c;, 0 0 0 1px #0a4475'
                          : 'inset 1px 3px 5px 0px #0000001c;',
                        '&:hover': {
                          borderColor: errors.clientIdFrom ? 'red' : '#0a4475',
                        },
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        height: '38px',
                        padding: '0 6px',
                      }),
                      input: (base) => ({
                        ...base,
                        margin: '0px',
                        padding: '0px',
                        color: '#333',
                        '& input': {
                          boxShadow: 'none !important',
                          border: 'none !important',
                        },
                      }),
                      indicatorsContainer: (base) => ({
                        ...base,
                        height: '38px',
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        padding: '4px',
                      }),
                      clearIndicator: (base) => ({
                        ...base,
                        padding: '4px',
                      }),
                      option: (base, { isFocused, isSelected }) => ({
                        ...base,
                        backgroundColor: isSelected
                          ? '#0a4475'
                          : isFocused
                            ? '#F0F0F0'
                            : 'white',
                        color: isSelected ? 'white' : '#333',
                        padding: '8px 12px',
                      }),
                      menu: (base) => ({
                        ...base,
                        marginTop: 0,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }),
                      singleValue: (base) => ({
                        ...base,
                        lineHeight: '1.2',
                      }),
                      placeholder: (base) => ({
                        ...base,
                        lineHeight: '1.2',
                      }),
                    }}
                  />
                  {!isDriver && (
                    <div className={classNames('mt-1', styles.small2)}>
                      <input
                        type="radio"
                        name="shipment_payment"
                        id="shipment_payment_1"
                        checked={formData.payer === 0}
                        onChange={() => handlePayerChange(0)}
                        disabled={isDisabledEdit || isClient}
                      />
                      <label htmlFor="shipment_payment_1">Плательщик</label>
                    </div>
                  )}
                </div>

                <div className="col-12 col-sm-6 mb-3">
                  <small className={classNames('d-block mb-1', styles.small2)}>
                    Получатель
                    {!isCargoModalFromReportTab ? '*' : ''}
                  </small>

                  <Select<ClientOption>
                    options={recipientSelectOptions}
                    value={
                      !formData.clientIdTo
                        ? RECIPIENT_NOT_SELECTED_OPTION
                        : isClient && currentCargo?.to
                          ? toClientOption(currentCargo.to)
                          : recipientSelectOptions.find(
                              (item) =>
                                String(item.value) === formData.clientIdTo
                            ) || toClientOption(currentCargo?.to)
                    }
                    onChange={(selectedOption) =>
                      handleSelectChange(selectedOption, 'clientIdTo')
                    }
                    isDisabled={isDisabledEdit || isClient}
                    placeholder="Выберите клиента"
                    noOptionsMessage={() => 'Клиент не найден'}
                    styles={{
                      control: (base, { isFocused }) => ({
                        ...base,
                        minHeight: '38px', // Уменьшаем минимальную высоту
                        height: '38px', // Уменьшаем фиксированную высоту
                        borderColor: errors.clientIdTo
                          ? 'red'
                          : isFocused
                            ? '#0a4475'
                            : '#ccc',
                        boxShadow: isFocused
                          ? 'inset 1px 3px 5px 0px #0000001c;, 0 0 0 1px #0a4475'
                          : 'inset 1px 3px 5px 0px #0000001c;',
                        '&:hover': {
                          borderColor: errors.clientIdTo ? 'red' : '#0a4475',
                        },
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        height: '38px', // Синхронизируем высоту
                        padding: '0 6px', // Уменьшаем padding
                      }),
                      input: (base) => ({
                        ...base,
                        margin: '0px',
                        padding: '0px',
                        color: '#333',
                        '& input': {
                          boxShadow: 'none !important',
                          border: 'none !important',
                        },
                      }),
                      indicatorsContainer: (base) => ({
                        ...base,
                        height: '38px',
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        padding: '4px',
                      }),
                      clearIndicator: (base) => ({
                        ...base,
                        padding: '4px',
                      }),
                      option: (base, { isFocused, isSelected }) => ({
                        ...base,
                        backgroundColor: isSelected
                          ? '#0a4475'
                          : isFocused
                            ? '#F0F0F0'
                            : 'white',
                        color: isSelected ? 'white' : '#333',
                        padding: '6px 12px',
                      }),
                      menu: (base) => ({
                        ...base,
                        marginTop: 0,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }),
                      singleValue: (base) => ({
                        ...base,
                        lineHeight: '1.2',
                      }),
                      placeholder: (base) => ({
                        ...base,
                        lineHeight: '1.2',
                      }),
                    }}
                  />
                  {!isDriver && (
                    <div className={classNames('mt-1', styles.small2)}>
                      <input
                        type="radio"
                        name="shipment_payment"
                        id="shipment_payment_2"
                        checked={formData.payer === 1}
                        onChange={() => handlePayerChange(1)}
                        disabled={isDisabledEdit || isClient}
                      />
                      <label htmlFor="shipment_payment_2">Плательщик</label>
                    </div>
                  )}
                </div>

                {!isDriver && (
                  <div className="col-12 col-sm-12 mb-3">
                    <div className={classNames('mb-2', styles.small2)}>
                      <input
                        type="radio"
                        name="shipment_payment"
                        id="shipment_payment_3"
                        checked={formData.payer === 2}
                        onChange={() => handlePayerChange(2)}
                        disabled={isDisabledEdit || isClient}
                      />
                      <label htmlFor="shipment_payment_3">
                        Плательщик 3-е лицо
                      </label>
                    </div>
                    {formData.payer === 2 && (
                      <div>
                        <Select
                          options={
                            isClient && currentInvoice
                              ? [
                                  // Для клиента: плательщиком всегда является сам клиент
                                  // Берем имя из объекта client из invoice
                                  {
                                    value:
                                      currentInvoice.client?.id || 'client',
                                    label:
                                      currentInvoice.client?.entity_name ||
                                      currentInvoice.client?.name ||
                                      'Клиент',
                                  },
                                ]
                              : [...clients]
                                  .sort((a, b) => {
                                    const nameA = (
                                      a.entity_name || a.name
                                    ).toLowerCase()
                                    const nameB = (
                                      b.entity_name || b.name
                                    ).toLowerCase()
                                    return nameA.localeCompare(nameB)
                                  })
                                  .map((item) => ({
                                    value: item.id,
                                    label: item.entity_name
                                      ? item.entity_name
                                      : item.name,
                                  }))
                          }
                          value={
                            formData.thirdId
                              ? isClient && currentInvoice
                                ? {
                                    value:
                                      currentInvoice.client?.id || 'client',
                                    label:
                                      currentInvoice.client?.entity_name ||
                                      currentInvoice.client?.name ||
                                      'Клиент',
                                  }
                                : clients
                                    .filter(
                                      (item) =>
                                        String(item.id) === formData.thirdId
                                    )
                                    .map((item) => ({
                                      value: item.id,
                                      label: item.entity_name
                                        ? item.entity_name
                                        : item.name,
                                    }))[0]
                              : null
                          }
                          onChange={(selectedOption) => {
                            if (isClient) return
                            const fakeEvent = {
                              target: {
                                name: 'thirdId',
                                value: selectedOption
                                  ? String(selectedOption.value)
                                  : '',
                              },
                            } as React.ChangeEvent<HTMLSelectElement>
                            handleChange(fakeEvent)
                          }}
                          isDisabled={isClient}
                          placeholder="Выберите компанию"
                          noOptionsMessage={() => 'Компания не найдена'}
                          isSearchable
                          styles={{
                            control: (base, { isFocused }) => ({
                              ...base,
                              minHeight: '38px', // Уменьшаем минимальную высоту
                              height: '38px', // Уменьшаем фиксированную высоту
                              borderColor: errors.clientIdTo
                                ? 'red'
                                : isFocused
                                  ? '#0a4475'
                                  : '#ccc',
                              boxShadow: isFocused
                                ? 'inset 1px 3px 5px 0px #0000001c;, 0 0 0 1px #0a4475'
                                : 'inset 1px 3px 5px 0px #0000001c;',
                              '&:hover': {
                                borderColor: errors.clientIdTo
                                  ? 'red'
                                  : '#0a4475',
                              },
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              height: '38px', // Синхронизируем высоту
                              padding: '0 6px', // Уменьшаем padding
                            }),
                            input: (base) => ({
                              ...base,
                              margin: '0px',
                              padding: '0px',
                              color: '#333',
                              '& input': {
                                boxShadow: 'none !important',
                                border: 'none !important',
                              },
                            }),
                            indicatorsContainer: (base) => ({
                              ...base,
                              height: '38px',
                            }),
                            dropdownIndicator: (base) => ({
                              ...base,
                              padding: '4px',
                            }),
                            clearIndicator: (base) => ({
                              ...base,
                              padding: '4px',
                            }),
                            option: (base, { isFocused, isSelected }) => ({
                              ...base,
                              backgroundColor: isSelected
                                ? '#0a4475'
                                : isFocused
                                  ? '#F0F0F0'
                                  : 'white',
                              color: isSelected ? 'white' : '#333',
                              padding: '6px 12px',
                            }),
                            menu: (base) => ({
                              ...base,
                              marginTop: 0,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            }),
                            singleValue: (base) => ({
                              ...base,
                              lineHeight: '1.2',
                            }),
                            placeholder: (base) => ({
                              ...base,
                              lineHeight: '1.2',
                            }),
                          }}
                          components={{
                            IndicatorSeparator: () => null,
                            DropdownIndicator: (props) => (
                              <div
                                {...props.innerProps}
                                style={{
                                  padding: '4px',
                                  marginRight: '10px',
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 14 4"
                                  fill="currentColor"
                                  style={{
                                    transition:
                                      'transform 0.2s ease, margin-bottom 0.2s ease',
                                    transform: props.selectProps.menuIsOpen
                                      ? 'rotate(180deg)'
                                      : 'rotate(0)',
                                    marginBottom: props.selectProps.menuIsOpen
                                      ? '-4px'
                                      : '0',
                                  }}
                                >
                                  <path d="M0.636,0.293C0.824,0.105,1.078,0,1.343,0S1.862,0.105,2.05,0.293L7,5.243L11.95,0.293c0.189-0.182,0.441-0.283,0.703-0.281c0.262,0.002,0.513,0.107,0.698,0.293c0.391,0.391,0.391,1.025,0,1.416l-5.657,5.657c-0.188,0.188-0.442,0.293-0.707,0.293s-0.519-0.105-0.707-0.293L0.636,1.709C0.247,1.318,0.247,0.684,0.636,0.293z" />
                                </svg>
                              </div>
                            ),
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className="col-12 mb-3">
                  <div className="row">
                    <div
                      className={classNames(
                        'col-12 mb-3 mb-sm-0',
                        isReportCargoLoadingTab && 'col-sm-6'
                      )}
                    >
                      <div className={classNames('mb-1', styles.small2)}>
                        Описание груза
                      </div>
                      <textarea
                        ref={textareaRefFull}
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className="w-100"
                        rows={3}
                        disabled={isClient}
                      />
                    </div>
                    {isReportCargoLoadingTab && (
                      <div className="col-12 col-sm-6">
                        <div className={classNames('mb-1', styles.small2)}>
                          Дата и время готовности
                        </div>
                        <div
                          className={classNames(
                            reportModalStyles.reportDate,
                            reportModalStyles.border5
                          )}
                        >
                          <DateTimePicker
                            id="cargo_readiness_at"
                            selectedDate={formData.readinessAt}
                            setSelectedDate={(date) =>
                              setFormData((prev) => ({
                                ...prev,
                                readinessAt: date,
                              }))
                            }
                            isValidateDate={false}
                            placeholder="дд.мм.гггг --:--"
                          />
                        </div>
                        <div className={classNames('mt-1', styles.small2)}>
                          <input
                            type="checkbox"
                            name="canBePickedUp"
                            id="cargo_can_be_picked_up"
                            checked={formData.canBePickedUp}
                            onChange={handleCheckboxChange}
                            disabled={isClient}
                          />
                          <label htmlFor="cargo_can_be_picked_up">
                            Готов, можно забирать
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-6 col-sm-3 mb-3 pr-1">
                  <div className={classNames('mb-1', styles.small2)}>
                    Объем, м<sup>3</sup>
                  </div>
                  <input
                    type="text"
                    name="volume"
                    value={formData.volume}
                    onChange={handleNumberChange}
                    autoComplete="off"
                    disabled={isClient}
                  />
                </div>

                <div className="col-6 col-sm-3 mb-3 pl-1">
                  <div className={classNames('mb-1', styles.small2)}>
                    Масса, кг
                  </div>
                  <input
                    type="text"
                    name="weight"
                    value={formData.weight}
                    onChange={handleNumberChange}
                    autoComplete="off"
                    disabled={isClient}
                  />
                </div>

                {!isDriver && (
                  <div className="col-6 col-sm-3 mb-3 pr-1">
                    <div className={classNames('mb-1', styles.small2)}>
                      Сумма:
                    </div>
                    <input
                      type="text"
                      name="amount"
                      value={formData.amount}
                      onChange={handleNumberChange}
                      autoComplete="off"
                      disabled={isClient}
                    />
                    <div
                      className={classNames(
                        styles.radioTabs,
                        styles.small3,
                        'd-flex flex-wrap radio-tabs justify-content-start'
                      )}
                    >
                      <div className="mt-1">
                        <input
                          name="currency"
                          id="currency_radio4"
                          type="radio"
                          value="EUR"
                          checked={true}
                          onChange={handleRadioChange}
                        />
                        <label className="ml-2 mr-0" htmlFor="currency_radio4">
                          {formData.currency}
                        </label>
                      </div>
                    </div>
                  </div>
                )}
                {!isDriver && (
                  <div className="col-6 col-sm-3 mb-3 pl-1">
                    <div className={classNames('mb-1', styles.small2)}>
                      Способ оплаты
                    </div>
                    <div className="d-flex flex-row flex-nowrap">
                      <select
                        className={styles.small2}
                        name="paymentMethod"
                        value={formData.paymentMethod}
                        onChange={handleChange}
                        disabled={isClient}
                      >
                        <option value="cash">Нал</option>
                        <option value="bank_transfer">Безнал</option>
                      </select>
                      {canEditIsPaid && (
                        <div
                          className={classNames(
                            styles.small2,
                            styles.shipmentPaidCheck,
                            'ml-2'
                          )}
                        >
                          <input
                            className="green-check"
                            type="checkbox"
                            name="isPaid"
                            id="shipment_paid"
                            checked={formData.isPaid}
                            onChange={handleCheckboxChange}
                          />
                          <label
                            className="pl-4"
                            title="Оплачено!"
                            htmlFor="shipment_paid"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="col-12 mb-3">
                  <div className="row align-items-end">
                    <div className="col mb-1 align-self-center">
                      <div className={classNames('mt-1', styles.small2)}>
                        <input
                          type="checkbox"
                          name="hasTn"
                          id="shipment_tn"
                          checked={formData.hasTn}
                          onChange={handleCheckboxChange}
                          disabled={isClient}
                        />
                        <label htmlFor="shipment_tn">Есть ТН</label>
                      </div>
                    </div>
                    <div
                      className="col-auto mb-1"
                      style={{ textAlign: 'right' }}
                    >
                      <small
                        className={classNames(
                          styles.small2,
                          'd-inline-block mr-2'
                        )}
                      >
                        Фото:
                      </small>
                      <div className={styles.photoActions}>
                        {photos.length > 0 && (
                          <PhotoProvider
                            key={(photos || [])
                              .map(
                                (p: any) => p?.id ?? (p?.image?.lg || p?.image)
                              )
                              .join('|')}
                            className={styles.photoProvider}
                            overlayRender={({ images, index, onClose }) => {
                              const currentImage = photos[index]
                              // Проверяем, может ли клиент удалить это фото (только свои)
                              const canDeleteImage =
                                !isClient ||
                                (isClient &&
                                  currentImage?.created_by &&
                                  currentImage.created_by === userId)
                              return (
                                <>
                                  {canDeleteImage && (
                                    <div
                                      className={classNames(
                                        styles.btnDeleteIcon
                                      )}
                                      onClick={() => {
                                        if (currentImage?.id) {
                                          setImageToDelete(currentImage.id)
                                          setIsVisibleDeleteIconModal(true)
                                        }
                                      }}
                                    />
                                  )}
                                  {isVisibleDeleteIconModal &&
                                    imageToDelete === currentImage?.id && (
                                      <div
                                        className={classNames(
                                          styles.deleteModal
                                        )}
                                      >
                                        <h2 className={styles.deletePhotoTitle}>
                                          Вы действительно хотите удалить фото?
                                        </h2>
                                        <div
                                          className={styles.deleteButtonWrapper}
                                        >
                                          <button
                                            type="button"
                                            onClick={() => {
                                              if (
                                                currentImage?.id &&
                                                // Дополнительная проверка для клиента
                                                (!isClient ||
                                                  (isClient &&
                                                    currentImage?.created_by ===
                                                      userId))
                                              ) {
                                                dispatch(
                                                  fetchDeleteCargoImage({
                                                    imageId: currentImage.id,
                                                    serviceId: currentCargo?.id,
                                                  })
                                                ).then(() => {
                                                  setIsVisibleDeleteIconModal(
                                                    false
                                                  )
                                                  setImageToDelete(null)
                                                  onClose()
                                                  if (
                                                    currentCargo?.id &&
                                                    !isCargoModalFromReportTab
                                                  ) {
                                                    dispatch(
                                                      fetchCargos()
                                                    ).then(() => {
                                                      if (currentInvoice?.id) {
                                                        dispatch(
                                                          fetchCurrentInvoices(
                                                            currentInvoice?.id
                                                          )
                                                        )
                                                      }
                                                    })
                                                  }
                                                })
                                              } else {
                                                toast.error(
                                                  'Ошибка: не удалось определить изображение для удаления'
                                                )
                                                setIsVisibleDeleteIconModal(
                                                  false
                                                )
                                                setImageToDelete(null)
                                              }
                                            }}
                                            className={classNames(
                                              styles.btn,
                                              styles.btnSmall,
                                              styles.btnMargin,
                                              'd-block',
                                              'd-sm-inline-block'
                                            )}
                                          >
                                            Удалить
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setIsVisibleDeleteIconModal(false)
                                              setImageToDelete(null)
                                            }}
                                            className={classNames(
                                              styles.btn,
                                              styles.btnSmall,
                                              styles.btnBlueOutline,
                                              'd-block d-sm-inline-block'
                                            )}
                                          >
                                            Отмена
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                </>
                              )
                            }}
                          >
                            <PhotoView
                              key={
                                photos[0]?.id ??
                                (photos[0]?.image?.lg || photos[0]?.image)
                              }
                              src={photos[0]?.image?.lg || photos[0]?.image}
                            >
                              <button
                                type="button"
                                className={classNames(
                                  styles.btn,
                                  styles.btnSmall2,
                                  styles.btnBlueOutline,
                                  styles.inlineButton
                                )}
                              >
                                <i
                                  className={classNames(
                                    styles.icon,
                                    styles.icon15,
                                    styles.iconPicture
                                  )}
                                />
                                {photos.length}
                              </button>
                            </PhotoView>
                            {photos.slice(1).map((photo: any) => (
                              <PhotoView
                                key={
                                  photo?.id ??
                                  (photo?.image?.lg || photo?.image)
                                }
                                src={photo?.image?.lg || photo?.image}
                              >
                                <img style={{ display: 'none' }} alt="" />
                              </PhotoView>
                            ))}
                          </PhotoProvider>
                        )}

                        <div
                          className={classNames(
                            styles.btn,
                            styles.btnSmall2,
                            styles.btnBlueOutline,
                            styles.loadGallery,
                            styles.inlineButton
                          )}
                        >
                          <b>{photos.length > 0 ? '+' : '+ Добавить'}</b>
                          <input
                            multiple={true}
                            onChange={handlePhotoChange}
                            type="file"
                            name="cargo_gallery"
                            id="cargo_gallery"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Поле для номера ТН */}
                  {formData.hasTn && (
                    <div
                      className={classNames('col-12 mb-3', styles.paddingZero)}
                    >
                      <input
                        type="text"
                        placeholder="Т/Н"
                        className="mt-2"
                        id="shipment_tn_text"
                        name="tnNumber"
                        value={formData.tnNumber}
                        onChange={handleTnNumberChange}
                        disabled={isClient}
                      />
                    </div>
                  )}
                </div>

                {/* Отображение загруженных фото */}
                {selectedPhoto.length > 0 && (
                  <div
                    className={classNames(
                      'col-12 mt-3 mb-3',
                      styles.photoContainer
                    )}
                  >
                    <PhotoProvider
                      key={selectedPhoto.join('|')}
                      className={styles.photoProvider}
                    >
                      {selectedPhoto?.map((photo) => (
                        <PhotoView key={photo} src={photo}>
                          <div className={styles.photoView}>
                            <img src={photo} alt="" />
                            <span
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeletePhoto(photo)
                              }}
                              className={styles.del}
                            >
                              ×
                            </span>
                          </div>
                        </PhotoView>
                      ))}
                    </PhotoProvider>
                  </div>
                )}
              </div>

              <div className="row mt-3">
                <div
                  className="col col-sm-8 mt-1 mb-1"
                  style={{ textAlign: 'left' }}
                >
                  {!isClient && (
                    <button
                      type="submit"
                      className={classNames(
                        'd-block d-sm-inline-block mb-1 mr-1',
                        styles.btn,
                        styles.btnSmall
                      )}
                      disabled={isPendingAddCargos}
                    >
                      <i
                        className={`${styles.icon} ${styles.iconCheckWhite}`}
                      ></i>{' '}
                      Сохранить
                    </button>
                  )}
                  {!isClient && (
                    <button
                      type="button"
                      onClick={handleSaveAndAddAnother}
                      className={classNames(
                        'd-block d-sm-inline-block mb-1',
                        styles.btn,
                        styles.btnSmall,
                        styles.btnBlue
                      )}
                    >
                      {' '}
                      <i
                        className={`${styles.icon} ${styles.iconCheckWhite}`}
                      ></i>{' '}
                      + Ещё
                    </button>
                  )}
                </div>
                <div
                  className="col-auto col-sm-4 mt-1 mb-1"
                  style={{ textAlign: 'right' }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      dispatch(setCurrentCargo(null))
                      dispatch(setIsOnlyCargo(false))
                      dispatch(setIsVisibleModalCargo(false))
                    }}
                    className={classNames(
                      'd-block d-sm-inline-block',
                      styles.btn,
                      styles.btnSmall,
                      styles.btnBlueOutline,
                      isClient ? styles.isClient : ''
                    )}
                  >
                    {isClient ? 'Закрыть' : 'Отмена'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CargoModal
